import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Tile } from "@carbon/react";
import { DimensionSlice } from "../../../types";
import { formatCurrency } from "../utils/kpiHelpers";
import { toTopNWithOther, DimensionBar } from "./chartData";
import "./analyticsCharts.scss";

interface DimensionBarChartProps {
  title: string;
  /** Slices of ONE dimension and ONE currency (the caller filters by the active currency). */
  data: DimensionSlice[];
  /** Formats a spend value; defaults to the slice currency, e.g. "USD 1,234". */
  valueFormatter?: (n: number) => string;
  /** How many bars to show before the remainder is folded into "Other". */
  topN?: number;
}

// Palette is `--cds-link-primary`/`--cds-support-*` with literal fallbacks — no `--cds-chart-*`
// tokens ship with this Carbon version, and an undefined `var()` voids the whole declaration.
const BAR_COLOR = "var(--cds-link-primary, #0f62fe)";
const OTHER_COLOR = "var(--cds-icon-secondary, #6f6f6f)";
const AXIS_COLOR = "var(--cds-text-secondary, #525252)";

const DEFAULT_TOP_N = 8;
const ROW_HEIGHT = 36;
const CHART_CHROME = 40;
// Category labels sit on the Y axis in a horizontal bar chart, so they need real room; anything
// longer than LABEL_MAX_CHARS is truncated (the tooltip carries the full name).
const Y_AXIS_WIDTH = 150;
const LABEL_MAX_CHARS = 22;

const truncate = (s: string) =>
  s.length > LABEL_MAX_CHARS ? `${s.slice(0, LABEL_MAX_CHARS - 1)}…` : s;

export const DimensionTooltip = ({
  active,
  payload,
  label,
  valueFormatter,
}: {
  active?: boolean;
  payload?: Array<{ payload: DimensionBar }>;
  label?: string;
  valueFormatter?: (n: number) => string;
}) => {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const bar = payload[0].payload;
  const format =
    valueFormatter ?? ((n: number) => formatCurrency(n, bar.currency));
  return (
    <div className="analytics-chart__tooltip">
      <p className="analytics-chart__tooltip-title">{label}</p>
      <p className="analytics-chart__tooltip-row">
        {`${t("analytics.charts.spend")}: ${format(bar.spend)}`}
      </p>
      <p className="analytics-chart__tooltip-row">
        {`${t("analytics.charts.awardsCount")}: ${bar.awards}`}
      </p>
    </div>
  );
};

const DimensionBarChart = ({
  title,
  data,
  valueFormatter,
  topN = DEFAULT_TOP_N,
}: DimensionBarChartProps) => {
  const { t } = useTranslation();
  const bars = toTopNWithOther(data, topN, t("analytics.charts.other"));

  if (bars.length === 0) {
    return (
      <Tile className="analytics-chart">
        <h3 className="analytics-chart__title">{title}</h3>
        <p className="analytics-chart__empty">{t("analytics.charts.noData")}</p>
      </Tile>
    );
  }

  const format =
    valueFormatter ?? ((n: number) => formatCurrency(n, bars[0].currency));

  return (
    <Tile className="analytics-chart">
      <h3 className="analytics-chart__title">{title}</h3>
      <ResponsiveContainer
        width="100%"
        height={bars.length * ROW_HEIGHT + CHART_CHROME}
      >
        <BarChart
          data={bars}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            stroke="var(--cds-border-subtle, #e0e0e0)"
          />
          <XAxis
            type="number"
            tickFormatter={format}
            fontSize={12}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={Y_AXIS_WIDTH}
            interval={0}
            tickFormatter={truncate}
            fontSize={12}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <Tooltip
            content={<DimensionTooltip valueFormatter={valueFormatter} />}
            cursor={{ fill: "var(--cds-layer-hover, #e8e8e8)" }}
          />
          <Bar dataKey="spend">
            {bars.map((bar) => (
              <Cell
                key={bar.id}
                fill={bar.id === "__other__" ? OTHER_COLOR : BAR_COLOR}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Tile>
  );
};

export default DimensionBarChart;
